import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { formatMoney } from "../lib/money";
import { toast } from "sonner";
import {
  Coins, Sparkles, ShieldCheck, ShieldAlert, Check, ArrowLeft, Star, Globe, IndianRupee,
} from "lucide-react";

/**
 * /dashboard/diploks/personal/topup
 * ----------------------------------------------------------------------
 * Universal personal Diploks top-up. Available to every eligible role
 * (delegate / chair / faculty / parent / volunteer / judge / event_manager
 * / school_admin / org_admin / super_admin). Sponsor / partner_institution
 * are gated server-side AND by ProtectedRoute role-list.
 *
 * Strict billing-entity routing:
 *   INR                                → Factskoot · Razorpay · GST
 *   USD AED EUR GBP HKD SGD MYR IDR    → Destineze · Stripe · UAE VAT 0
 *
 * UX rule: INR is kept visually separated from the international row so
 * a foreign user is never tempted to "save by paying in INR" (Factskoot
 * can only invoice Indian buyers and Razorpay can only accept INR).
 */
const INTL_CURRENCIES = [
  { code: "USD", glyph: "$" },
  { code: "AED", glyph: "AED" },
  { code: "EUR", glyph: "€" },
  { code: "GBP", glyph: "£" },
  { code: "HKD", glyph: "HK$" },
  { code: "SGD", glyph: "S$" },
  { code: "MYR", glyph: "RM" },
  { code: "IDR", glyph: "Rp" },
];

export default function PersonalDiploksTopUpPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [currency, setCurrency] = useState("INR");
  const [selectedTier, setSelectedTier] = useState(null);
  const [accept_terms, setAcceptTerms] = useState(false);
  // R4C — Voucher + promo state
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [voucherMsg, setVoucherMsg] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [banners, setBanners] = useState([]);
  const [accept_notice, setAcceptNotice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Strict Geo-IP region — feeds the currency-pill filter so a user
  // outside India never sees the INR pill (and vice-versa).
  const [geoRegion, setGeoRegion] = useState(null);
  const [allowedCurrencies, setAllowedCurrencies] = useState(null);

  // Default currency from geo
  const loadGeo = useCallback(async () => {
    try {
      const r = await api.get("/payments/pricing-geo");
      const region = r.data?.geo?.region;
      const primary = r.data?.geo?.primary_currency;
      const allowed = r.data?.geo?.allowed_currencies;
      setGeoRegion(region || null);
      setAllowedCurrencies(Array.isArray(allowed) && allowed.length
        ? allowed.map((c) => c.toUpperCase()) : null);
      if (region === "international" && primary) setCurrency(primary);
    } catch { /* keep INR default */ }
  }, []);
  useEffect(() => { loadGeo(); }, [loadGeo]);

  // Load packs
  const loadPacks = useCallback(async (cur) => {
    setLoading(true);
    try {
      const r = await api.get(`/diploks/personal/topup-plans?currency=${cur}`);
      setPlans(r.data);
      // The server may have snapped us to a different currency if our
      // geo doesn't allow `cur` — honour the snap so the UI stays consistent.
      const serverCur = (r.data?.currency || cur).toUpperCase();
      if (serverCur !== cur) {
        setCurrency(serverCur);
      }
      // Backfill geo-region + allowed-currencies from this endpoint too
      // (covers the case where pricing-geo isn't called yet).
      if (r.data?.geo_region) setGeoRegion(r.data.geo_region);
      if (Array.isArray(r.data?.allowed_currencies) && r.data.allowed_currencies.length) {
        setAllowedCurrencies(r.data.allowed_currencies.map((c) => c.toUpperCase()));
      }
      const packs = r.data?.packs || [];
      if (packs.length) {
        const def = packs.find((p) => p.popular) || packs[0];
        setSelectedTier(def.tier_key);
      }
      // R4C — fetch promo banners for this currency
      try {
        const b = await api.get(
          `/diploks/promos/banners?currency=${encodeURIComponent(cur)}&wallet_owner_type=personal`,
        );
        setBanners(b.data?.banners || []);
      } catch (_) {
        setBanners([]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPacks(currency); }, [currency, loadPacks]);

  const selected = useMemo(
    () => (plans?.packs || []).find((p) => p.tier_key === selectedTier),
    [plans, selectedTier],
  );

  // R4C — Redeem voucher
  const redeemVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherBusy(true);
    setVoucherMsg(null);
    try {
      const r = await api.post("/diploks/vouchers/redeem", {
        code: voucherCode.trim(), wallet_scope: "personal",
      });
      setVoucherMsg({ ok: true, text: r.data?.message
          || `${r.data?.diploks_credited} Diploks added.` });
      setVoucherCode("");
      window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
    } catch (e) {
      setVoucherMsg({ ok: false, text: formatApiError(e) });
    } finally {
      setVoucherBusy(false);
    }
  };

  // R4C — Validate promo (preview bonus)
  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoBusy(true);
    setPromoError(null);
    try {
      const r = await api.post("/diploks/promos/validate", {
        code: promoCode.trim(),
        tier_key: selected?.tier_key,
        currency,
      });
      setPromoPreview(r.data);
    } catch (e) {
      setPromoPreview(null);
      setPromoError(formatApiError(e));
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => {
    setPromoCode("");
    setPromoPreview(null);
    setPromoError(null);
  };

  const startTopup = async () => {
    if (!selected) return;
    if (!accept_terms || !accept_notice) {
      toast.error("Please accept the Diploks terms and platform-credit notice.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/diploks/personal/topup/start", {
        tier_key: selected.tier_key,
        currency,
        accept_terms,
        accept_diploks_notice: accept_notice,
        promo_code: promoPreview?.ok ? promoCode.trim() : undefined,
      });
      window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));

      // Stripe path — full-page redirect.
      if (r.data?.gateway === "stripe" && r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }

      // Razorpay path — open the Razorpay checkout overlay.
      if (r.data?.gateway === "razorpay") {
        const ptId = r.data.payment_transaction_id;
        const orderId = r.data.razorpay_order_id;
        const keyId = r.data.razorpay_key_id;

        // MOCK mode — backend signals there are no live Razorpay keys.
        // Immediately call confirm so the wallet credits in preview.
        if (r.data.mode === "mock" || !keyId || !orderId) {
          await confirmTopup({
            payment_transaction_id: ptId,
            mock: true,
          });
          return;
        }

        await loadRazorpay();
        if (!window.Razorpay) {
          toast.error("Razorpay couldn't load. Please refresh and try again.");
          return;
        }
        const rzp = new window.Razorpay({
          key: keyId,
          order_id: orderId,
          amount: r.data.amount_minor,
          currency: "INR",
          name: "Diplomexa",
          description: `Personal Diploks — ${selected.label}`,
          notes: { payment_transaction_id: ptId,
                   kind: "diploks_personal_topup" },
          handler: async (rzpResp) => {
            await confirmTopup({
              payment_transaction_id: ptId,
              razorpay_order_id: rzpResp.razorpay_order_id,
              razorpay_payment_id: rzpResp.razorpay_payment_id,
              razorpay_signature: rzpResp.razorpay_signature,
            });
          },
          modal: {
            ondismiss: () => {
              toast.message("Top-up cancelled — your wallet was not charged.");
              setSubmitting(false);
            },
          },
          theme: { color: "#0A192F" },
        });
        rzp.open();
        return;
      }

      toast.error("Unsupported gateway. Please contact support.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmTopup = async (payload) => {
    try {
      const r = await api.post("/diploks/personal/topup/confirm", payload);
      const dipl = r.data?.diploks_added || 0;
      toast.success(`${dipl} Diploks credited!`, {
        description: `Balance: ${r.data?.balance_after}`,
      });
      window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
      navigate("/dashboard/diploks/personal/wallet");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <p className="text-sm text-slate-500" data-testid="personal-topup-loading">
          Loading top-up plans…
        </p>
      </DashboardLayout>
    );
  }

  if (!plans) return null;

  const packs = plans.packs || [];
  const isManualReview = geoRegion === "manual_review";

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="personal-diploks-topup-page">
        {/* VPN / manual-review banner — surfaces ONLY when the geo
            provider flags the request as datacenter / proxy / VPN.
            Nudges the user to disable VPN before checkout so they see
            their region's currency, GST and billing entity. */}
        {isManualReview && (
          <div
            className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
            data-testid="personal-topup-vpn-banner"
            role="alert"
          >
            <ShieldAlert className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-900">
                VPN or proxy detected — disable it to view local prices
              </p>
              <p className="mt-1 text-xs text-amber-800 leading-relaxed">
                For payment safety, Diplomexa needs to verify your
                country of residence before showing tax-correct prices.
                Please disable your VPN / proxy and refresh — your
                regional currency, GST and billing entity will load
                automatically.
              </p>
            </div>
          </div>
        )}

        <Link
          to="/dashboard/diploks/personal/wallet"
          className="text-xs text-slate-500 hover:text-[#0F4C81] inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> Back to My Diploks
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Personal Top-Up
            </p>
            <h1 className="mt-2 text-3xl font-bold text-[#0A192F] tracking-tight">
              Power up your Diploks
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-xl">
              Choose a pack to unlock AI paper reviews, opening speech
              feedback, premium certificates and more.
            </p>
          </div>
        </div>

        {/* Currency switcher — STRICT Geo-IP routing. Indian users see
            INR (Factskoot · Razorpay · GST) only; foreign users see
            their Stripe-supported currencies only.  Dev / unknown geos
            see the full catalogue so testing / first-load isn't blocked.
            An EMPTY allowed list (e.g. manual_review) also opens the
            catalogue — the /topup/start endpoint will refuse the actual
            charge with a professional message. */}
        {(() => {
          const allow = Array.isArray(allowedCurrencies) && allowedCurrencies.length
            ? allowedCurrencies : null;
          const showInr = !allow || allow.includes("INR");
          const visibleIntl = INTL_CURRENCIES.filter(
            (c) => !allow || allow.includes(c.code),
          );
          const showSeparator = showInr && visibleIntl.length > 0;
          return (
            <div
              className="mt-6 flex flex-wrap items-center gap-2"
              data-testid="personal-topup-currency-switcher"
            >
              {showInr && (
                <button
                  type="button"
                  onClick={() => setCurrency("INR")}
                  data-testid="personal-topup-currency-inr"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    currency === "INR"
                      ? "bg-[#0A192F] text-white border-[#0A192F]"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <IndianRupee className="h-3 w-3" /> INR
                  <span className="text-[9px] uppercase tracking-wider opacity-70">
                    India · GST
                  </span>
                </button>
              )}

              {showSeparator && (
                <>
                  <span className="h-5 w-px bg-slate-200 mx-1" aria-hidden="true" />
                  <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">
                    International
                  </span>
                </>
              )}

              {visibleIntl.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setCurrency(c.code)}
                  data-testid={`personal-topup-currency-${c.code.toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    currency === c.code
                      ? "bg-[#0A192F] text-white border-[#0A192F]"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Globe className="h-3 w-3" /> {c.code}
                </button>
              ))}

              {geoRegion === "international" && (
                <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold"
                      data-testid="personal-topup-geo-hint">
                  Showing currencies available in your region
                </span>
              )}
            </div>
          );
        })()}

        {/* Billing entity strip */}
        <div
          className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-700 flex items-center gap-2"
          data-testid="personal-topup-entity-strip"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-[#0F4C81]" />
          Billed by <span className="font-semibold">{plans.billing_entity_name}</span>{" "}
          via <span className="font-semibold capitalize">{plans.gateway}</span>.
        </div>

        {/* R4C — Promotional banners (event/org/sponsor advertisements) */}
        {banners.length > 0 ? (
          <div
            data-testid="personal-topup-promo-banners"
            className="mt-5 grid gap-3 md:grid-cols-2"
          >
            {banners.map((b) => (
              <div
                key={b.promo_id}
                data-testid={`personal-topup-promo-banner-${b.promo_id}`}
                className="rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 shadow-sm"
              >
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">
                  {b.campaign_type?.replaceAll("_", " ") || "Promo"}
                </p>
                <h3 className="mt-1 text-sm font-semibold text-amber-950">
                  {b.advertisement_title}
                </h3>
                {b.advertisement_subtitle ? (
                  <p className="mt-1 text-xs text-amber-800">
                    {b.advertisement_subtitle}
                  </p>
                ) : null}
                <div className="mt-2 flex items-center justify-between">
                  <code className="rounded bg-white px-2 py-0.5 text-[11px] font-mono text-amber-900 border border-amber-200">
                    {b.promo_code_mask}
                  </code>
                  <span className="text-[11px] text-amber-700 font-semibold">
                    {b.bonus_type === "percentage"
                      ? `+${b.bonus_value}%`
                      : `+${b.bonus_value} Diploks`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Pack grid */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packs.map((p) => {
            const isSel = p.tier_key === selectedTier;
            return (
              <button
                key={p.tier_key}
                type="button"
                onClick={() => setSelectedTier(p.tier_key)}
                data-testid={`personal-topup-pack-${p.tier_key}`}
                className={`text-left rounded-2xl border p-5 transition-all ${
                  isSel
                    ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/30 bg-white"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                {p.popular && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider bg-[#D4AF37] text-[#0A192F] rounded-full px-2 py-0.5 mb-3">
                    <Star className="h-3 w-3" /> Popular
                  </span>
                )}
                <p className="text-sm font-bold text-[#0A192F]">{p.label}</p>
                {p.positioning && (
                  <p className="text-[10px] uppercase tracking-wider text-[#0F4C81] font-bold mt-0.5">
                    {p.positioning}
                  </p>
                )}
                <p className="mt-2 text-3xl font-bold tabular-nums text-[#0A192F]">
                  {p.diploks?.toLocaleString()}
                  <span className="text-sm text-slate-500 ml-1">Diploks</span>
                </p>
                {p.bonus > 0 && (
                  <p className="text-xs text-emerald-700 font-semibold mt-1">
                    + {p.bonus} Bonus Diploks
                  </p>
                )}
                <p className="mt-3 text-xs text-slate-600 leading-relaxed">
                  {p.summary}
                </p>
                <div className="mt-4 flex items-baseline justify-between">
                  <p
                    className="text-xl font-bold text-[#0A192F]"
                    data-testid={`personal-topup-price-${p.tier_key}`}
                  >
                    {formatMoney(p.price, currency)}
                  </p>
                  {isSel && (
                    <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                      <Check className="h-3 w-3" /> Selected
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Order summary + acceptance */}
        {selected && (
          <div
            className="mt-8 rounded-2xl border border-slate-200 bg-white p-6"
            data-testid="personal-topup-summary"
          >
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
              Order summary
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <Row label={selected.label} value={formatMoney(selected.price, currency)} />
              <Row label="Diploks" value={(selected.diploks || 0).toLocaleString()} />
              {selected.bonus > 0 && (
                <Row label="Bonus" value={`+${selected.bonus}`} accent="emerald" />
              )}
              <Row label="Total Diploks credited" value={(selected.total_diploks || selected.diploks || 0).toLocaleString()} bold />
              {/* R4C — promo bonus preview row */}
              {promoPreview?.ok ? (
                <Row
                  label={`Bonus Diploks (${promoPreview.campaign_name || "promo"})`}
                  value={`+${promoPreview.bonus_diploks}`}
                  accent="emerald"
                />
              ) : null}
            </div>

            {/* R4C — Promo code input + bonus preview */}
            <div
              data-testid="personal-topup-promo-section"
              className="mt-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">
                Have a promo code?
              </p>
              {promoPreview?.ok ? (
                <div
                  data-testid="personal-topup-promo-applied"
                  className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900"
                >
                  <p className="font-semibold">{promoPreview.campaign_name}</p>
                  <p className="mt-1">{promoPreview.message}</p>
                  <button
                    type="button"
                    data-testid="personal-topup-promo-remove"
                    onClick={clearPromo}
                    className="mt-2 text-[11px] text-emerald-700 underline"
                  >
                    Remove promo
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="e.g. EUROMUN25"
                    data-testid="personal-topup-promo-input"
                    className="flex-1 min-w-[200px] rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs uppercase tracking-wider"
                  />
                  <button
                    type="button"
                    data-testid="personal-topup-promo-apply"
                    disabled={promoBusy || !promoCode.trim() || !selected}
                    onClick={validatePromo}
                    className="rounded-md bg-amber-700 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white disabled:bg-amber-300"
                  >
                    {promoBusy ? "Checking…" : "Apply"}
                  </button>
                </div>
              )}
              {promoError ? (
                <p
                  data-testid="personal-topup-promo-error"
                  className="mt-2 text-xs text-rose-700"
                >
                  {promoError}
                </p>
              ) : null}
            </div>

            {/* R4C — Voucher redeem (independent of top-up payment) */}
            <div
              data-testid="personal-topup-voucher-section"
              className="mt-3 rounded-xl border border-violet-200 bg-violet-50/60 p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-violet-800">
                Have a Diploks gift voucher?
              </p>
              <p className="mt-1 text-[11px] text-violet-700">
                Redeem to add Diploks directly to your wallet — no payment needed.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  placeholder="e.g. DIP-AB7K-92LM"
                  data-testid="personal-topup-voucher-input"
                  className="flex-1 min-w-[200px] rounded-md border border-violet-300 bg-white px-3 py-1.5 text-xs uppercase tracking-wider"
                />
                <button
                  type="button"
                  data-testid="personal-topup-voucher-redeem"
                  disabled={voucherBusy || !voucherCode.trim()}
                  onClick={redeemVoucher}
                  className="rounded-md bg-violet-700 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white disabled:bg-violet-300"
                >
                  {voucherBusy ? "Redeeming…" : "Redeem voucher"}
                </button>
              </div>
              {voucherMsg ? (
                <p
                  data-testid={`personal-topup-voucher-msg-${voucherMsg.ok ? "ok" : "err"}`}
                  className={`mt-2 text-xs ${voucherMsg.ok ? "text-emerald-700" : "text-rose-700"}`}
                >
                  {voucherMsg.text}
                </p>
              ) : null}
            </div>

            <div className="mt-5 space-y-2 text-xs">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accept_terms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  data-testid="personal-topup-accept-terms"
                  className="mt-0.5"
                />
                <span>
                  I accept the{" "}
                  <Link to="/terms" target="_blank" className="text-[#0F4C81] underline">
                    Terms & Conditions
                  </Link>
                  ,{" "}
                  <Link to="/billing-notice" target="_blank" className="text-[#0F4C81] underline">
                    Billing Notice
                  </Link>{" "}
                  and{" "}
                  <Link to="/refund-policy" target="_blank" className="text-[#0F4C81] underline">
                    Refund Policy
                  </Link>
                  .
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accept_notice}
                  onChange={(e) => setAcceptNotice(e.target.checked)}
                  data-testid="personal-topup-accept-notice"
                  className="mt-0.5"
                />
                <span>
                  I understand that <strong>Diploks are Diplomexa platform
                  credits</strong> used to unlock eligible digital services.
                  Diploks are <strong>not cash, not cryptocurrency, not
                  e-money, not a stored-value wallet, not an investment
                  product, not transferable, not withdrawable, not
                  resellable, and cannot be converted into money.</strong>
                </span>
              </label>
            </div>

            <button
              type="button"
              disabled={submitting || !accept_terms || !accept_notice}
              onClick={startTopup}
              data-testid="personal-topup-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white font-bold text-xs uppercase tracking-wider px-4 py-3 rounded-md hover:bg-[#0F2C50] disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {submitting ? "Initialising…" : `Top up ${selected.diploks} Diploks`}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value, bold, accent }) {
  const accentCls = accent === "emerald" ? "text-emerald-700" : "text-[#0A192F]";
  return (
    <div className="flex justify-between py-1.5">
      <span className={`text-slate-600 ${bold ? "font-semibold" : ""}`}>{label}</span>
      <span className={`${accentCls} ${bold ? "font-bold" : "font-medium"}`}>{value}</span>
    </div>
  );
}


// Lazy-load Razorpay checkout script once. Mirrors the org top-up page.
let _rzpLoading = null;
function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  if (_rzpLoading) return _rzpLoading;
  _rzpLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return _rzpLoading;
}
